import { useState, useEffect } from 'react';
import type { ChangeEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Divider,
  Chip,
  Avatar,
  Checkbox,
  Paper,
  IconButton,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  ColorLens as ColorLensIcon,
} from '@mui/icons-material';
import { doc, getDoc, addDoc, updateDoc, collection, query, where, getDocs, Timestamp, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useTenant } from '../providers/TenantProvider';
import { serviceInputSchema, serviceUpdateSchema } from '../lib/db/schemas';
import { serviceConverter } from '../lib/db/converters';
import { ZodError } from 'zod';
import type { ServiceCategory, Location, User } from '../lib/db/types';

interface ServiceCategoryWithId extends ServiceCategory {
  id: string;
}

interface LocationWithId extends Location {
  id: string;
}

interface UserWithId extends User {
  id: string;
}

interface FormErrors {
  [key: string]: string;
}

const CURRENCIES = [
  { value: 'USD', label: 'USD ($)', symbol: '$' },
  { value: 'EUR', label: 'EUR (€)', symbol: '€' },
  { value: 'GBP', label: 'GBP (£)', symbol: '£' },
  { value: 'CAD', label: 'CAD (CA$)', symbol: 'CA$' },
  { value: 'AUD', label: 'AUD (A$)', symbol: 'A$' },
  { value: 'JPY', label: 'JPY (¥)', symbol: '¥' },
];

const PRESET_COLORS = [
  '#1976d2', '#388e3c', '#d32f2f', '#7b1fa2', '#fbc02d',
  '#f57c00', '#5d4037', '#455a64', '#c62828', '#2e7d32',
  '#00695c', '#283593', '#ad1457', '#4a148c', '#b71c1c',
];

export function ServiceFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { tenantId, role } = useTenant();

  const isEditMode = !!id;
  const canEdit = role === 'owner' || role === 'manager';

  // Loading states
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [providersLoading, setProvidersLoading] = useState(true);

  // Data states
  const [categories, setCategories] = useState<ServiceCategoryWithId[]>([]);
  const [locations, setLocations] = useState<LocationWithId[]>([]);
  const [providers, setProviders] = useState<UserWithId[]>([]);

  // Alert states
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form errors
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    imageUrl: '',
    duration: 60,
    price: '',
    currency: 'USD',
    paymentPolicy: 'pay_later' as 'not_required' | 'pay_later' | 'prepay',
    visibility: 'public' as 'public' | 'private',
    isActive: true,
    availabilitiesType: 'flexible' as 'flexible' | 'fixed',
    attendantsNumber: 1,
    locationId: '',
    categoryId: '',
    color: '#1976d2',
    displayOrder: 0,
    providerIds: [] as string[],
  });

  // Load initial data
  useEffect(() => {
    if (!tenantId) return;

    const loadData = async () => {
      try {
        // Load categories
        setCategoriesLoading(true);
        const categoriesQuery = query(
          collection(db, 'serviceCategories'),
          where('tenantId', '==', tenantId),
          where('isActive', '==', true),
          orderBy('displayOrder')
        );
        const categoriesSnap = await getDocs(categoriesQuery);
        const categoriesData = categoriesSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as ServiceCategoryWithId[];
        setCategories(categoriesData);
        setCategoriesLoading(false);

        // Load locations
        setLocationsLoading(true);
        const locationsQuery = query(
          collection(db, 'locations'),
          where('tenantId', '==', tenantId),
          orderBy('name')
        );
        const locationsSnap = await getDocs(locationsQuery);
        const locationsData = locationsSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as LocationWithId[];
        setLocations(locationsData);
        setLocationsLoading(false);

        // Load providers (users with coach or staff role)
        setProvidersLoading(true);
        const usersQuery = query(
          collection(db, 'tenants', tenantId, 'memberships'),
          where('roles', 'array-contains-any', ['coach', 'staff', 'owner', 'manager'])
        );
        const usersSnap = await getDocs(usersQuery);
        const usersData = usersSnap.docs
          .map((d) => {
            const data = d.data();
            return {
              id: d.id,
              ...data,
              role: data.primaryRole,
            } as UserWithId;
          })
          .sort((a, b) => `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(`${b.firstName || ''} ${b.lastName || ''}`));
        setProviders(usersData);
        setProvidersLoading(false);

        // If edit mode, load existing service
        if (isEditMode && id) {
          setLoading(true);
          const serviceDoc = await getDoc(doc(db, 'services', id).withConverter(serviceConverter));
          if (serviceDoc.exists()) {
            const serviceData = serviceDoc.data();
            setFormData({
              name: serviceData.name || '',
              description: serviceData.description || '',
              imageUrl: serviceData.imageUrl || '',
              duration: serviceData.duration || 60,
              price: serviceData.price ? (serviceData.price / 100).toString() : '',
              currency: (serviceData.currency || 'USD').toUpperCase(),
              paymentPolicy: serviceData.paymentPolicy || (serviceData.price ? 'pay_later' : 'not_required'),
              visibility: serviceData.visibility || 'public',
              isActive: serviceData.isActive ?? true,
              availabilitiesType: serviceData.availabilitiesType || 'flexible',
              attendantsNumber: serviceData.attendantsNumber || 1,
              locationId: serviceData.locationId || '',
              categoryId: serviceData.categoryId || '',
              color: serviceData.color || '#1976d2',
              displayOrder: serviceData.displayOrder || 0,
              providerIds: serviceData.providerIds || [],
            });
          } else {
            setError('Service not found');
          }
          setLoading(false);
        }
      } catch (err: any) {
        console.error('Error loading data:', err);
        setError('Failed to load data');
        setLoading(false);
        setCategoriesLoading(false);
        setLocationsLoading(false);
        setProvidersLoading(false);
      }
    };

    loadData();
  }, [tenantId, id, isEditMode]);

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    // Name is required
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    // Duration is required and must be at least 5 minutes
    if (!formData.duration || formData.duration < 5) {
      errors.duration = 'Duration must be at least 5 minutes';
    }

    // If price is set, currency is required
    if (formData.price && parseFloat(formData.price) > 0 && !formData.currency) {
      errors.currency = 'Currency is required when price is set';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!canEdit) {
      setError('You do not have permission to edit services');
      return;
    }

    if (!validateForm()) {
      setError('Please fix the errors below');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const price = formData.price
        ? Math.round(parseFloat(formData.price) * 100)
        : undefined;

      const serviceData = {
        tenantId,
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        duration: formData.duration,
        price: price || undefined,
        currency: price ? formData.currency.toLowerCase() : undefined,
        paymentPolicy: price ? formData.paymentPolicy : 'not_required',
        visibility: formData.visibility,
        isActive: formData.isActive,
        availabilitiesType: formData.availabilitiesType,
        attendantsNumber: formData.attendantsNumber,
        locationId: formData.locationId || undefined,
        categoryId: formData.categoryId || undefined,
        color: formData.color,
        displayOrder: formData.displayOrder,
        providerIds: formData.providerIds,
        imageUrl: formData.imageUrl.trim() || undefined,
      };

      if (isEditMode && id) {
        // Validate with update schema
        const validated = serviceUpdateSchema.parse(serviceData);
        await updateDoc(doc(db, 'services', id), {
          ...validated,
          updatedAt: Timestamp.now(),
        });
        setSuccess('Service updated successfully');
      } else {
        // Validate with input schema
        const validated = serviceInputSchema.parse(serviceData);
        await addDoc(collection(db, 'services'), {
          ...validated,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
        setSuccess('Service created successfully');
        // Clear form for next creation
        if (!isEditMode) {
          setTimeout(() => navigate('/services'), 1500);
        }
      }
    } catch (err: any) {
      console.error('Error saving service:', err);
      if (err instanceof ZodError) {
        const zodErrors: FormErrors = {};
        err.errors.forEach((e) => {
          const path = e.path.join('.');
          zodErrors[path] = e.message;
        });
        setFormErrors(zodErrors);
        setError('Validation failed. Please check the form.');
      } else {
        setError(err.message || 'Failed to save service');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleProviderToggle = (providerId: string) => {
    setFormData((prev) => ({
      ...prev,
      providerIds: prev.providerIds.includes(providerId)
        ? prev.providerIds.filter((id) => id !== providerId)
        : [...prev.providerIds, providerId],
    }));
  };

  const getProviderDisplayName = (provider: UserWithId): string => {
    if (provider.firstName || provider.lastName) {
      return `${provider.firstName || ''} ${provider.lastName || ''}`.trim();
    }
    return provider.email;
  };

  const getProviderInitials = (provider: UserWithId): string => {
    if (provider.firstName && provider.lastName) {
      return `${provider.firstName[0]}${provider.lastName[0]}`.toUpperCase();
    }
    if (provider.firstName) {
      return provider.firstName[0].toUpperCase();
    }
    if (provider.lastName) {
      return provider.lastName[0].toUpperCase();
    }
    return provider.email[0].toUpperCase();
  };

  if (loading || categoriesLoading || locationsLoading || providersLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!canEdit) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">
          You do not have permission to manage services. Only owners and managers can access this page.
        </Alert>
        <Box mt={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/services')}
          >
            Back to Services
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/services')}
        >
          Back
        </Button>
        <Typography variant="h4" component="h1">
          {isEditMode ? 'Edit Service' : 'Create Service'}
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Main Form */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Box component="form" noValidate>
                {/* Basic Info Section */}
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12}>
                    <TextField
                      label="Service Name"
                      value={formData.name}
                      onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, name: e.target.value })}
                      fullWidth
                      required
                      error={!!formErrors.name}
                      helperText={formErrors.name || 'Required'}
                      disabled={saving}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Description"
                      value={formData.description}
                      onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, description: e.target.value })}
                      fullWidth
                      multiline
                      rows={3}
                      disabled={saving}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Image URL"
                      value={formData.imageUrl}
                      onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, imageUrl: e.target.value })}
                      fullWidth
                      placeholder="https://example.com/image.jpg"
                      disabled={saving}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Service Details Section */}
                <Typography variant="h6" gutterBottom>
                  Service Details
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Duration (minutes)"
                      type="number"
                      value={formData.duration}
                      onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, duration: parseInt(e.target.value) || 0 })}
                      fullWidth
                      required
                      error={!!formErrors.duration}
                      helperText={formErrors.duration || 'Minimum 5 minutes'}
                      inputProps={{ min: 5 }}
                      disabled={saving}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Display Order"
                      type="number"
                      value={formData.displayOrder}
                      onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                      fullWidth
                      helperText="Lower numbers appear first"
                      disabled={saving}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Price"
                      type="number"
                      value={formData.price}
                      onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, price: e.target.value })}
                      fullWidth
                      inputProps={{ step: '0.01', min: '0' }}
                      placeholder="0.00"
                      disabled={saving}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Currency"
                      value={formData.currency}
                      onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, currency: e.target.value })}
                      fullWidth
                      error={!!formErrors.currency}
                      helperText={formErrors.currency || (formData.price ? 'Required when price is set' : 'Optional')}
                      disabled={saving}
                    >
                      {CURRENCIES.map((currency) => (
                        <MenuItem key={currency.value} value={currency.value}>
                          {currency.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Payment Policy"
                      value={formData.paymentPolicy}
                      onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, paymentPolicy: e.target.value as 'not_required' | 'pay_later' | 'prepay' })}
                      fullWidth
                      helperText={formData.paymentPolicy === 'prepay' ? 'Requires Stripe checkout before confirmation' : 'Controls booking-time payment handling'}
                      disabled={saving || !formData.price}
                    >
                      <MenuItem value="not_required">No payment required</MenuItem>
                      <MenuItem value="pay_later">Book now, pay later</MenuItem>
                      <MenuItem value="prepay">Require prepayment</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <FormLabel sx={{ mb: 1 }}>Visibility</FormLabel>
                      <RadioGroup
                        row
                        value={formData.visibility}
                        onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, visibility: e.target.value as 'public' | 'private' })}
                      >
                        <FormControlLabel
                          value="public"
                          control={<Radio disabled={saving} />}
                          label="Public"
                        />
                        <FormControlLabel
                          value="private"
                          control={<Radio disabled={saving} />}
                          label="Private"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.isActive}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, isActive: e.target.checked })}
                          disabled={saving}
                        />
                      }
                      label={formData.isActive ? 'Active' : 'Inactive'}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Booking Settings Section */}
                <Typography variant="h6" gutterBottom>
                  Booking Settings
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <FormLabel sx={{ mb: 1 }}>Availabilities Type</FormLabel>
                      <RadioGroup
                        value={formData.availabilitiesType}
                        onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, availabilitiesType: e.target.value as 'flexible' | 'fixed' })}
                      >
                        <FormControlLabel
                          value="flexible"
                          control={<Radio disabled={saving} />}
                          label="Flexible"
                        />
                        <FormControlLabel
                          value="fixed"
                          control={<Radio disabled={saving} />}
                          label="Fixed"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Attendants Number"
                      type="number"
                      value={formData.attendantsNumber}
                      onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, attendantsNumber: parseInt(e.target.value) || 1 })}
                      fullWidth
                      helperText="1 = Individual, >1 = Group class"
                      inputProps={{ min: 1 }}
                      disabled={saving}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Location"
                      value={formData.locationId}
                      onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, locationId: e.target.value })}
                      fullWidth
                      disabled={saving}
                    >
                      <MenuItem value="">
                        <Box component="em">None</Box>
                      </MenuItem>
                      {locations.map((location) => (
                        <MenuItem key={location.id} value={location.id}>
                          {location.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Category"
                      value={formData.categoryId}
                      onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, categoryId: e.target.value })}
                      fullWidth
                      disabled={saving}
                    >
                      <MenuItem value="">
                        <Box component="em">None</Box>
                      </MenuItem>
                      {categories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Display Section */}
                <Typography variant="h6" gutterBottom>
                  Display Settings
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Calendar Color
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {PRESET_COLORS.map((color) => (
                        <IconButton
                          key={color}
                          onClick={() => setFormData({ ...formData, color })}
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: color,
                            border: formData.color === color ? '3px solid #000' : 'none',
                            '&:hover': { bgcolor: color, opacity: 0.8 },
                          }}
                          disabled={saving}
                        >
                          {formData.color === color && <ColorLensIcon sx={{ color: '#fff', fontSize: 16 }} />}
                        </IconButton>
                      ))}
                    </Box>
                    <TextField
                      label="Custom Color"
                      value={formData.color}
                      onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, color: e.target.value })}
                      fullWidth
                      placeholder="#1976d2"
                      helperText="Hex color code (e.g., #1976d2)"
                      disabled={saving}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Providers Section */}
                <Typography variant="h6" gutterBottom>
                  Providers
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select users who can provide this service
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  {providers.length === 0 ? (
                    <Typography color="text.secondary" align="center">
                      No providers available. Add users with Coach or Staff role.
                    </Typography>
                  ) : (
                    <Grid container spacing={1}>
                      {providers.map((provider) => (
                        <Grid item xs={12} sm={6} md={4} key={provider.id}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              p: 1,
                              borderRadius: 1,
                              bgcolor: formData.providerIds.includes(provider.id) ? 'action.selected' : 'transparent',
                              cursor: saving ? 'default' : 'pointer',
                              '&:hover': saving ? {} : { bgcolor: 'action.hover' },
                            }}
                            onClick={() => !saving && handleProviderToggle(provider.id)}
                          >
                            <Checkbox
                              checked={formData.providerIds.includes(provider.id)}
                              onChange={() => handleProviderToggle(provider.id)}
                              disabled={saving}
                            />
                            <Avatar
                              src={provider.photoUrl}
                              alt={getProviderDisplayName(provider)}
                              sx={{ width: 32, height: 32 }}
                            >
                              {getProviderInitials(provider)}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" noWrap>
                                {getProviderDisplayName(provider)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {provider.email}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                  {formData.providerIds.length > 0 && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="body2" color="text.secondary">
                        Selected: {formData.providerIds.length} provider(s)
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {formData.providerIds.map((providerId) => {
                          const provider = providers.find((p) => p.id === providerId);
                          if (!provider) return null;
                          return (
                            <Chip
                              key={providerId}
                              size="small"
                              avatar={
                                <Avatar src={provider.photoUrl} sx={{ width: 20, height: 20 }}>
                                  {getProviderInitials(provider)}
                                </Avatar>
                              }
                              label={getProviderDisplayName(provider)}
                              onDelete={() => handleProviderToggle(providerId)}
                              disabled={saving}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Paper>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar - Actions */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ position: 'sticky', top: 20 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Actions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  onClick={handleSubmit}
                  disabled={saving}
                  fullWidth
                  size="large"
                >
                  {isEditMode ? 'Update Service' : 'Create Service'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/services')}
                  disabled={saving}
                  fullWidth
                >
                  Cancel
                </Button>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle2" gutterBottom>
                Quick Preview
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    bgcolor: formData.color,
                  }}
                />
                <Typography variant="body2" noWrap>
                  {formData.name || 'New Service'}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {formData.duration} min
              </Typography>
              {formData.price && (
                <Typography variant="body2" color="text.secondary">
                  {formData.currency === 'EUR' ? '€' : formData.currency === 'GBP' ? '£' : formData.currency === 'JPY' ? '¥' : '$'}
                  {parseFloat(formData.price).toFixed(2)}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Chip
                  size="small"
                  label={formData.visibility}
                  color={formData.visibility === 'public' ? 'success' : 'default'}
                />
                <Chip
                  size="small"
                  label={formData.isActive ? 'Active' : 'Inactive'}
                  color={formData.isActive ? 'success' : 'default'}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
